import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, Check } from '@phosphor-icons/react';

function SearchableFlavorSelect({
    id,
    label,
    options,
    selectedValue,
    onSelect,
    placeholder,
    noResultsText,
    disabled,
    loading,
}) {
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find((option) => option.value === selectedValue);
    const [query, setQuery] = useState(selectedOption?.label || '');
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    useEffect(() => {
        setQuery(selectedOption?.label || '');
    }, [selectedOption?.label]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (!rootRef.current?.contains(event.target)) {
                setIsOpen(false);
                setQuery(selectedOption?.label || '');
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [selectedOption?.label]);

    const filteredOptions = useMemo(() => {
        const normalizedQuery = query === selectedOption?.label ? '' : query.trim().toLowerCase();
        if (!normalizedQuery) return options;
        return options.filter((option) => `${option.label} ${option.value}`.toLowerCase().includes(normalizedQuery));
    }, [options, query, selectedOption?.label]);

    const openMenu = () => {
        if (disabled) return;
        setIsOpen(true);
        setQuery('');
        setHighlightedIndex(0);
    };

    const chooseOption = (option) => {
        onSelect(option);
        setQuery(option.label);
        setIsOpen(false);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            setQuery(selectedOption?.label || '');
            return;
        }
        if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
            event.preventDefault();
            openMenu();
            return;
        }
        if (!isOpen || filteredOptions.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((index) => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            chooseOption(filteredOptions[highlightedIndex]);
        }
    };

    return (
        <div className="field combo" ref={rootRef}>
            <label className="label" htmlFor={id}>{label}</label>
            <div className="combo__control">
                <input
                    ref={inputRef}
                    id={id}
                    className="input"
                    type="text"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls={`${id}-options`}
                    aria-expanded={isOpen}
                    aria-label={label}
                    autoComplete="off"
                    disabled={disabled}
                    value={query}
                    placeholder={loading ? 'Loading flavors...' : placeholder}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setHighlightedIndex(0);
                        setIsOpen(true);
                    }}
                    onFocus={openMenu}
                    onKeyDown={handleKeyDown}
                />
                <CaretDown className={`combo__chevron${isOpen ? ' is-open' : ''}`} size={18} aria-hidden="true" />
            </div>
            {isOpen && (
                <div className="combo__menu" id={`${id}-options`} role="listbox" aria-label={label}>
                    {filteredOptions.length > 0 ? filteredOptions.map((option, index) => (
                        <button
                            className={`combo__option${option.value === selectedValue ? ' is-selected' : ''}${index === highlightedIndex ? ' is-highlighted' : ''}`}
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={option.value === selectedValue}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => chooseOption(option)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                        >
                            <span>{option.label}</span>
                            {option.value === selectedValue && <Check size={18} aria-hidden="true" />}
                        </button>
                    )) : <p className="combo__empty">{noResultsText}</p>}
                </div>
            )}
        </div>
    );
}

export default SearchableFlavorSelect;
